import {
  countHybrid,
  countStorage,
  PowerFlowGraphMode,
  countMeter,
  countEnerclic,
  ProductModelService,
  ConnextData,
  AzzurroHubDataType,
  DeviceType,
  AHmonitoringModeEnum,
  AHplantDetectEnum
} from "ngx-core-services";

import { SystemDefinition } from "../../state/system.state";

export const checkAndUpdateVirtualSystemStructure = (system: SystemDefinition): SystemDefinition => {
  const hasHybrid = countHybrid(system) > 0;
  const hasStorage = countStorage(system) > 0;

  let structure;
  if (hasHybrid || hasStorage) {
    structure = PowerFlowGraphMode.WithBattery;
  } else if (countMeter(system) || countEnerclic(system)) {
    structure = PowerFlowGraphMode.WithImportExport;
  } else {
    structure = PowerFlowGraphMode.OnlyGeneratedPower;
  }

  return {
    ...system,
    metadata: {
      /** Default to Europe/Rome */
      ...system.metadata,
      tz: system.metadata?.tz ?? 2,
      structure,
    },
  };
}

export const checkAndUpdateConnextSystemStructure = (system: SystemDefinition): SystemDefinition => {
  const connextData = system.data as Partial<ConnextData>;
  if (!connextData) {
    throw new Error('No connext data available');
  }

  const sensor_mode = connextData.config?.sensor_mode;
  const structure = sensor_mode === 4 ? PowerFlowGraphMode.WithBattery : PowerFlowGraphMode.WithImportExport;

  return {
    ...system,
    metadata: {
      /** Default to Europe/Rome */
      ...system.metadata,
      tz: system.metadata?.tz ?? 2,
      structure,
    },
  };
}

export const checkAndUpdateAzzurroHubSystemStructure = (system: SystemDefinition): SystemDefinition => {
  const azzurroHubData = system.data as Partial<AzzurroHubDataType>;
  if (!azzurroHubData) {
    throw new Error('No azzurroHub data available');
  }

  // NEW LOGIC FOR AZZURRO HUB STRUCTURE DETERMINATION
  const plantDetectType = azzurroHubData.config?.plantDetectType;

  if (plantDetectType) {
    let structure: PowerFlowGraphMode = PowerFlowGraphMode.OnlyGeneratedPower;

    switch (plantDetectType) {
      case AHplantDetectEnum.hyd:
        structure = PowerFlowGraphMode.WithBattery;
        break;
      case AHplantDetectEnum.importExport:
        structure = PowerFlowGraphMode.WithImportExport;
        break;
      case AHplantDetectEnum.solar:
        structure = PowerFlowGraphMode.OnlyGeneratedPower;
        break;
      default:
        structure = PowerFlowGraphMode.WithImportExport;
    }

    return {
      ...system,
      metadata: {
        /** Default to Europe/Rome */
        ...system.metadata,
        tz: system.metadata?.tz ?? 2,
        structure,
      },
    };

  } else {
    // Fallback to old logic to remove when all hubs are updated
    let structure: PowerFlowGraphMode = PowerFlowGraphMode.OnlyGeneratedPower;

    const monitoringMode = azzurroHubData.config?.monitoringMode;

    switch (monitoringMode) {
      case AHmonitoringModeEnum.invMaster:
      case AHmonitoringModeEnum.pwrMagic:
        structure = PowerFlowGraphMode.WithBattery;
        break;
      case AHmonitoringModeEnum.intSensor:
      case AHmonitoringModeEnum.meter:
        structure = PowerFlowGraphMode.WithImportExport;
        break;
      case AHmonitoringModeEnum.meter:
        structure = PowerFlowGraphMode.WithImportExport;
        break;
      case AHmonitoringModeEnum.none:
        structure = PowerFlowGraphMode.OnlyGeneratedPower;
        break;
      default:
        structure = PowerFlowGraphMode.OnlyGeneratedPower;
    }

    return {
      ...system,
      metadata: {
        /** Default to Europe/Rome */
        ...system.metadata,
        tz: system.metadata?.tz ?? 2,
        structure,
      },
    };
  }
}

export const checkAndUpdateDevices = (system: SystemDefinition): SystemDefinition => {
  if (system.source === 'aws') {
    /**
   * Its important to filter devices that have not been uninstalled
   */
    system.metadata = {
      ...((system.metadata ?? {}) as any),
      verifiedDevices: [...(system.aws?.devices ?? []).filter(device => device.disinstallationTime === null)],
      verifiedUninstallations: [...(system.aws?.devices ?? []).filter(device => device.disinstallationTime !== null)],
    }

    if( system.monitoringType === 'AzzurroHub' ) {
      // If AzzurroHub is not present, add it
      if (!system.devices.find(device => device.type === 'azzurro-hub')) {
        system.devices = [...system.devices, {
          id: system.data?.loggerId ?? '',
          name: system.data?.loggerId ?? '',
          type: 'azzurro-hub' as DeviceType,
        }];
      }
    } else {

      const isConnextPref = system.aws?.plantPref?.monitorPref === 'CONNEXT' || system.aws?.config?.monitoringType === 'CONNEXT';
      system.devices = isConnextPref ? ((system.data as Partial<ConnextData>)?.devices ?? []).map(device => {
        const product = new ProductModelService(device.sn);
        return {
          id: device.sn,
          name: device.sn,
          type: product.type?.name!,
        }
      }) : (system.metadata?.verifiedDevices ?? []).map(device => {
        const product = new ProductModelService(device.sn);
        return {
          id: device.sn,
          name: device.sn,
          type: product.type?.name!,
        }
      })

      if (isConnextPref) {
        const connextData = system.data as Partial<ConnextData>;

        system.devices.push({
          id: connextData.info?.sn?.uid ?? connextData.id ?? '',
          name: connextData.id ?? '',
          type: 'connext',
        })
      }
    }

  } else if (system.configuration.connextId) {
    system.metadata = {
      ...((system.metadata ?? {}) as any),
      verifiedDevices: [...((system.data as Partial<ConnextData>)?.devices ?? [])],
      verifiedUninstallations: [],
    }

    system.devices = (system.metadata?.verifiedDevices ?? []).map(device => {
      const product = new ProductModelService(device.sn);
      return {
        id: device.sn,
        name: device.sn,
        type: product.type?.name!,
      }
    })

    const connextData = system.data as Partial<ConnextData>;

    system.devices.push({
      id: connextData.info?.sn?.uid ?? connextData.id ?? '',
      name: connextData.id ?? '',
      type: 'connext',
    })
  } else {
    /**
     * @important
     * This is a hack to trigger "metadata" when the list of devices changes
     * so as to update the blocks
     *
     * @attention
     * Do not trigger anything other than metadata
     */

    system.metadata = {
      ...((system.metadata ?? {}) as any),
      firebaseDevices: [...(system?.devices ?? [])],
    }
  }

  return system;
}

export const checkAndUpdateSystemStructure = (system: SystemDefinition): SystemDefinition => {
  system = checkAndUpdateDevices(system);

  if (system.monitoringType === 'Connext' ) {
    return checkAndUpdateConnextSystemStructure(system);
  }

  if(system.monitoringType === 'AzzurroHub') {
    return checkAndUpdateAzzurroHubSystemStructure(system);
  }
  return checkAndUpdateVirtualSystemStructure(system);
}
